import React, { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { useToast } from "~/utils/Toast"

import { formatCurrency, capitalize } from "~/utils/helpers"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import CatchTable from "~/library/CatchTable"
import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiPacks } from "~/routes"
import { ArrowLeft } from "lucide-react"
import NavigationLink from "~/library/NavigationLink"
import {
  getPacksBackPath,
  getPacksBackTitle,
  clearPacksNavigationState,
} from "~/utils/navigationState"
import Pack from "~/types/Pack"
import PackItem from "~/types/PackItem"
import LineItem from "~/types/LineItem"

const PackView = () => {
  const [deletingPackItem, setDeletingPackItem] = useState<string | null>(null)
  const navigate = useNavigate()
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const endpoint = apiPacks.show.path({ venueSlug, id: id! })
  const {
    data: pack,
    refetch,
    isLoading,
  } = useFetchResource<{ pack: Pack }, "pack">(endpoint, "pack", {})
  const toast = useToast()

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const formatPrice = (amount: number) => {
    return formatCurrency({ cents: amount, currency_iso: "USD" })
  }

  const togglePackActive = () => {
    const data = {
      venue_slug: venueSlug,
      id: id!,
      pack: { active: !pack.active },
    }

    const req = apiPacks.update({ data })
    req
      .then(() => {
        toast(`Pack ${pack.active ? "deactivated" : "activated"} successfully`, "success")
        refetch()
      })
      .catch((res) => {
        toast(
          `Error updating pack. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
      })
  }

  const deletePack = () => {
    const req = apiPacks.destroy({ data: { venueSlug, id: id! } })
    req
      .then(() => {
        toast("Pack deleted successfully", "success")
        clearPacksNavigationState()
        navigate("/packs")
      })
      .catch((res) => {
        toast(
          `Error deleting pack. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
      })
  }

  const calculatePackTotal = () => {
    if (!pack.items) return 0
    return pack.items.reduce((total: number, item: PackItem) => {
      return total + item.lineItem.amountCents * item.quantity
    }, 0)
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getPacksBackPath())}
              aria-label={`Go back to ${getPacksBackTitle()}`}
              title={`Go back to ${getPacksBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>{`${pack.name}${!pack.active ? " (Inactive)" : ""}`}</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          items={[
            {
              label: "Edit Pack",
              to: `/packs/${id}/edit`,
            },
            {
              label: pack.active ? "Deactivate" : "Activate",
              onClick: togglePackActive,
            },
            {
              label: "Delete Pack",
              className: "text-danger",
              onClick: () => setDeletingPackItem("pack"),
            },
          ]}
        />
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Pack Details">
            <div className="row">
              {renderDetail("Pack Name", pack.name, 4)}
              {renderDetail("Price", formatPrice(pack.priceCents), 4)}
              {renderDetail("Status", pack.active ? "Active" : "Inactive", 4)}
              {pack.description && renderDetail("Description", pack.description, 12)}
            </div>
          </CatchCard>

          <CatchCard title="Pack Items">
            {!pack.items || pack.items.length === 0 ? (
              <div className="text-center p-3 text-muted">
                No items in this pack.
                <Link to={`/packs/${id}/edit`} className="btn btn-primary ms-2">
                  Add Items
                </Link>
              </div>
            ) : (
              <CatchTable
                data={pack.items}
                columns={[
                  {
                    key: "lineItem",
                    header: "Line Item",
                    formatter: (lineItem: LineItem) => (
                      <NavigationLink type="line-items" to={`/line-items/${lineItem.id}`}>
                        {lineItem.name}
                      </NavigationLink>
                    ),
                  },
                  {
                    key: "lineItem",
                    header: "Category",
                    formatter: (lineItem: LineItem) => capitalize(lineItem.categoryName) || "—",
                  },
                  {
                    key: "quantity",
                    header: "Quantity",
                    formatter: (quantity: number) => quantity,
                  },
                  {
                    key: "lineItem",
                    header: "Unit Price",
                    formatter: (lineItem: LineItem) => formatPrice(lineItem.amountCents),
                  },
                  {
                    key: "subtotal" as keyof PackItem,
                    header: "Subtotal",
                    formatter: (_, data: PackItem) =>
                      formatPrice(data.lineItem.amountCents * data.quantity),
                  },
                ]}
              />
            )}

            {pack.items && pack.items.length > 0 && (
              <div className="row mt-3">
                <div className="col-12">
                  <div className="d-flex justify-content-end">
                    <strong>Pack Total: {formatPrice(calculatePackTotal())}</strong>
                  </div>
                </div>
              </div>
            )}
          </CatchCard>
        </div>

        <div className="col-4">
          <CatchCard title="Pack Information">
            <div className="row">
              {renderDetail("Set Price", formatPrice(pack.priceCents), 12)}
              {renderDetail("Calculated Total", formatPrice(calculatePackTotal()), 12)}
              {renderDetail("Status", pack.active ? "Active" : "Inactive", 12)}
            </div>
          </CatchCard>

          <CatchCard title="Internal Details">
            <div className="row">
              {renderDetail("Created on", moment(pack?.createdAt).format("MMM Do YYYY hh:mma"), 12)}
              {pack?.updatedAt &&
                renderDetail(
                  "Updated on",
                  moment(pack?.updatedAt).format("MMM Do YYYY hh:mma"),
                  12
                )}
            </div>
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deletingPackItem === "pack"}
        onClose={() => setDeletingPackItem(null)}
        onConfirm={deletePack}
        title="Delete Pack?"
        description="This action cannot be undone. Are you sure you want to delete this pack?"
      />
    </LoadingWrapper>
  )
}

export default PackView
